import { colors, themeConfig } from "./theme";

// Theme custom variables, REF: https://mui.com/customization/theming/#custom-variables
export type CustomVariables = {
  zIndex: {
    baseLayer: number;
    mediumLayer: number;
    topLayer: number;
    extraLayer: number;
  };
  storeSize: {
    width: string;
    tabWidth: string;
    bigTabWidth: string;
    requestTabWidth: string;
    height: string;
    tabHeight: string;
    bigTabHeight: string;
    requestTabHeight: string;
    widgetWidth: string;
    widgetHeight: string;
    tradeWidgetHeight: string;
    widgetMaxWidth: string;
    widgetMinWidth: string;
  };
  navBarSize: {
    height: string;
    widgetHeight: string;
  };
  defaultBoxRadius: number | string;
  largeBoxRadius: number | string;
  mediumBoxRadius: number | string;
  buttonSmallRadius: number | string;
  buttonDefaultRadius: number | string;
  buttonRoundedRadius: number | string;
  smallGutterMargin: string;
  gutterMargin: string;
  mediumGutterMargin: string;
  bigGutterMargin: string;
  cedeLinkBigContentMargin: string;
  cedeLinkSmallContentMargin: string;
  cedeLinkBigContentMaxWidth: string;
  inputRadius: {
    default: number;
    rounded: number;
  };
  imgSize: {
    small: string;
    medium: string;
    large: string;
  };
  iconSize: {
    small: string;
    medium: string;
    large: string;
  };
  linkLogoWidth: {
    small: string;
    medium: string;
    large: string;
  };
  linkLogoHeight: {
    small: string;
    medium: string;
    large: string;
  };
  circleSize: {
    small: string;
  };
  inputWidth: {
    small: string;
  };
  opacity: {
    alert: number;
    legend: number;
  };
  textSize: {
    small: string;
    medium: string;
    large: string;
  };
  iconUnderlayMargin: number;
  portfolioItemWidth: string;
  portfolioItemHeight: string;
  linkInactiveFilterWidth: string;
  linkActiveFilterWidth: string;
  txHistoryItem: {
    height: number;
  };
  txHistoryList: {
    dateLineHeight: number;
    dateLineMargin: number; // between CEXs for the same date
    txDate: {
      margin: number;
      height: number;
    };
    txPlace: {
      margin: number;
      height: number;
    };
    txItem: {
      collapsed: number;
      toggled: number;
    };
  };
  dappListItemHeight: number;
  gradientButtonBackground: string;
  connectAccount: {
    background: string;
    backgroundRGB: string;
    hoverBackground: string;
    borderColor: string;
    buttonBorderColor: string;
    borderRadius: string;
  };
  drawerBackdropColor: string;
  drawerBackground: string;
  darkBackground: string;
  selectBackground: string;
  scrollbarThumb: string;
  scrollbarTrack: string;
  selectorTrigger: {
    border: string;
    borderActive: string;
    background: string;
    hoverBackground: string;
    iconBackground: string;
    chevronColor: string;
  };
  tableRowHoverColor: string;
  contrast: {
    shadePrimary08: string;
    shadePrimary16: string;
    shadePrimary30: string;
    shade10: string;
    shade20: string;
    shade70: string;
    shade87: string;
  };
  notice: {
    background: string;
    color: string;
  };
  containerBorder: string;
  componentBlur: string;
  textGradient: {
    primary: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
  medalColors: {
    gold: string;
    silver: string;
    bronze: string;
    default: string;
  };
  medalRowBackgrounds: {
    gold: string;
    silver: string;
    bronze: string;
    default: string;
  };
  campaignStatus: {
    future: {
      borderColor: string;
      color: string;
    };
    live: {
      borderColor: string;
      color: string;
    };
    past: {
      borderColor: string;
      color: string;
    };
    paused: {
      borderColor: string;
      color: string;
    };
  };
  animation: {
    cardHover: string;
  };
  status: {
    background: string;
  };
  mobileBackground: string;
  informationCard: {
    backgroundColor: string;
    border: string;
    titleColor: string;
    textColor: string;
  };
  usdtColor: string;
};

export type PaletteColorOptions = {
  light?: string;
  main: string;
  dark?: string;
  contrastText?: string;
};

export type PaletteText = {
  primary: string;
  secondary: string;
  disabled: string;
};

export type PaletteBackground = {
  paper: string;
  default: string;
  lightPaper: string;
  darkPaper: string;
  component: string;
  help: string;
};

export type PaletteAction = {
  disabled: string;
  disabledBackground: string;
};

type ColorsObject = typeof colors;

export type PaletteOptions = {
  primary: PaletteColorOptions;
  secondary: PaletteColorOptions;
  warning: PaletteColorOptions;
  error: PaletteColorOptions;
  success: PaletteColorOptions;
  logoBorderColor: string;
  text: PaletteText;
  background: PaletteBackground;
  action: PaletteAction;
  mode: "dark" | "light";
} & ColorsObject;

export type Breakpoints = {
  values: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
  };
};

export type ThemeConfig = Omit<typeof themeConfig, "palette"> & {
  palette: PaletteOptions;
};

export type TypographyVariant =
  // Standard variants
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "overline"
  // Component variants
  | "badgeLabel"
  | "buttonLarge"
  | "buttonMedium"
  | "buttonSmall"
  | "chip"
  | "helperText"
  | "inputText"
  | "inputLabel"
  | "menuItem"
  | "tooltip"
  | "campaignStatus"
  | "notice"
  | "linkTitle"
  | "linkSubtitle";
